import { useMemo } from 'react';

import { useGetEventRoom, useGetUser } from 'database';
import { useQueryState } from 'nuqs';
import { useLocalStorage } from 'usehooks-ts';

import { useHandleGlobalVisible } from '@hooks/use-handle-global-visible';
import { isAppApproaching } from '@utils/Common';

import { ReviewRequestModal, TogetherReadyModal } from './components';
import CardocCouponModal from './components/CardocCouponModal';
import { KnockCouponModal } from './components/KnockCouponModal';
import { WebToAppBottomSheet } from './components/WebToAppBottomSheet';

const ONE_DAY = 1000 * 60 * 60 * 24;

const isAppApproach = isAppApproaching();

const TOMORROW = new Date(new Date().getTime() + ONE_DAY);

export const GlobalModal = () => {
  const isHomeABTest = true;

  const { user, isLoading: isUserLoading } = useGetUser();
  const { data: eventRoom, isFetching: isEventRoomLoading } = useGetEventRoom(
    user?.togetherEvent?.eventRoom.id,
  );
  const [knockCode] = useQueryState('knock_code', {
    defaultValue: '',
  });

  const [cardocCouponEvent] = useQueryState('cardoc_coupon_event', {
    defaultValue: '',
  });

  const [unreceivedCoupon, setUnreceivedCoupon] = useLocalStorage(
    'last-unreceived-coupon-code',
    '',
  );
  const [userInfoExist] = useLocalStorage('user-info', false);

  const keyConditions: KeyCondition[] = useMemo(
    () => [
      {
        key: 'knockCoupon',
        conditions: [knockCode.length > 0],
        expiredOption: {
          showAfterExpired: true,
        },
      },
      {
        key: 'visitCoupon',
        conditions: [unreceivedCoupon === 'visit-coupon-5000' && userInfoExist],
        expiredOption: {
          showAfterExpired: false,
        },
      },
      {
        key: 'reviewRequest',
        conditions: [Boolean(user?.canWriteReview)],
        expiredOption: {
          showAfterExpired: false,
        },
      },
      {
        key: 'togetherReady',
        conditions: [Boolean(user && eventRoom && eventRoom.isAnyMemberReady(user.uid))],
        expiredOption: {
          showAfterExpired: false,
        },
      },
      {
        key: 'webToApp',
        conditions: [!isAppApproach],
        unavailablePaths: [/\/application/, /\/booking/],
      },
      {
        key: 'cardocCoupon',
        conditions: [cardocCouponEvent === 'true'],
        expiredOption: {
          showAfterExpired: true,
        },
        availablePaths: ['/'],
      },
    ],
    [user, eventRoom, unreceivedCoupon, userInfoExist, knockCode, cardocCouponEvent],
  );

  const {
    visibleStates: [
      knockCouponVisible,
      visitCouponVisible,
      reviewRequestVisible,
      togetherReadyVisible,
      webToAppVisible,
      cardocCouponVisible,
    ],
    close,
    isVisible,
    savedState,
    setSavedStateWithKey,
    forceMount,
  } = useHandleGlobalVisible({
    keyConditions,
    isLoading: isUserLoading || isEventRoomLoading,
  });

  if (isUserLoading || isEventRoomLoading || !isVisible) return null;

  return (
    <>
      {cardocCouponVisible ? (
        <CardocCouponModal close={close} />
      ) : knockCouponVisible ? (
        <KnockCouponModal close={close} />
      ) : visitCouponVisible ? (
        <></>
      ) : reviewRequestVisible ? (
        <ReviewRequestModal close={close} />
      ) : togetherReadyVisible ? (
        <TogetherReadyModal close={close} />
      ) : webToAppVisible ? (
        <WebToAppBottomSheet
          close={close}
          onTodayNoShowClick={() => {
            close();
            setSavedStateWithKey('webToApp', { hideDate: TOMORROW });
          }}
        />
      ) : (
        <></>
      )}
    </>
  );
};
